import { ArrowRight, Check, KeyRound, LoaderCircle, LockKeyhole, Mail, ShieldCheck } from 'lucide-react'
import { FormEvent, useState } from 'react'
import { requireSupabase } from '../lib/supabase'
import { HelpTooltip } from './HelpTooltip'

type LoginScreenProps = {
  configured: boolean
}

export function LoginScreen({ configured }: LoginScreenProps) {
  const [method, setMethod] = useState<'link' | 'password'>('link')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [linkSent, setLinkSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!configured) return
    setBusy(true)
    setMessage('')
    try {
      const client = requireSupabase()
      if (method === 'password') {
        const { error } = await client.auth.signInWithPassword({ email: email.trim().toLowerCase(), password })
        if (error) throw error
        return
      }

      const { error } = await client.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: window.location.origin, shouldCreateUser: true },
      })
      if (error) throw error
      setLinkSent(true)
      setMessage('Lien envoyé. Ouvrez l’email reçu puis cliquez sur le lien sécurisé pour accéder à Suivi Aziz.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Connexion impossible.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login-page">
      <section className="login-brand-panel">
        <img src="/assets/brand/sablux-ci-logo.png" alt="Sablux Côte d’Ivoire" className="login-logo" />
        <div className="login-promise">
          <span className="eyebrow light">VOTRE ESPACE PRIVÉ</span>
          <h1>Le parcours administratif,<br />clair à chaque étape.</h1>
          <p>
            Suivez les projets, revenez dans Excel quand vous le souhaitez et gardez la maîtrise de chaque changement.
          </p>
          <ul>
            <li><Check size={18} /> Une vue simple de ce qui est fait, en cours et à venir</li>
            <li><Check size={18} /> Aucun fichier retourné ne modifie les données sans votre accord</li>
            <li><Check size={18} /> Une aide contextuelle pour comprendre les notions moins familières</li>
          </ul>
        </div>
        <div className="login-brand-foot">Sablux Côte d’Ivoire · Suivi administratif</div>
      </section>

      <section className="login-form-panel">
        <div className="login-form-wrap">
          <div className="secure-mark"><ShieldCheck size={26} /></div>
          <span className="eyebrow">SUIVI AZIZ</span>
          <h2>Bienvenue</h2>
          <p className="form-intro">Connectez-vous à votre espace de travail sécurisé.</p>

          <div className="auth-switch" role="tablist" aria-label="Méthode de connexion">
            <button className={method === 'link' ? 'active' : ''} onClick={() => { setMethod('link'); setMessage(''); setLinkSent(false) }} type="button">
              <Mail size={17} /> Lien par email
            </button>
            <button className={method === 'password' ? 'active' : ''} onClick={() => { setMethod('password'); setMessage(''); setLinkSent(false) }} type="button">
              <KeyRound size={17} /> Mot de passe
            </button>
          </div>
          {method === 'link' ? (
            <div className="auth-method-help">
              Connexion sans mot de passe
              <HelpTooltip text="Nous envoyons un lien de connexion à usage unique. Aucun code à recopier." label="Comment fonctionne le lien par email ?" align="right" />
            </div>
          ) : null}

          <form onSubmit={submit}>
            <label>
              Adresse email
              <span className="input-wrap"><Mail size={18} /><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></span>
            </label>

            {method === 'password' ? (
              <label>
                Mot de passe
                <span className="input-wrap"><LockKeyhole size={18} /><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required /></span>
              </label>
            ) : null}

            <button className="primary-button login-submit" type="submit" disabled={busy || !configured}>
              {busy ? <LoaderCircle size={19} className="spin" /> : null}
              {method === 'link' ? (linkSent ? 'Renvoyer le lien' : 'Recevoir mon lien') : 'Ouvrir Suivi Aziz'}
              {!busy ? <ArrowRight size={19} /> : null}
            </button>
          </form>

          {message ? <p className="auth-message" role="status">{message}</p> : null}
          {!configured ? <p className="auth-message error">La connexion Supabase doit être configurée avant ouverture.</p> : null}
          <p className="privacy-note"><LockKeyhole size={15} /> Accès réservé aux comptes autorisés. Les parties prenantes n’ont pas de compte.</p>
        </div>
      </section>
    </main>
  )
}
